import {inject} from 'vue';
import type {PrismicDocument} from '@prismicio/client';
import {callWithNuxt} from '#app';
import {useLogger, useRoute, useSpanWrap, useNuxtApp, navigateTo, usePrismicLang, useLocalePrefix} from '#imports';
import {FETCH_LINKS, PRISMIC_CLIENT} from '~/app/keys';
import usePrismicRedirect from '~~/modules/redirect/usePrismicRedirect';

/**
 * Retrieve document from Prismic repository.
 * @param documentType  Document type.
 * @param uid           Document UID.
 */

export type PrismicDocumentResult<T> = {
    document: T | null;
    redirected: boolean;
};

export default async function usePrismicDocument<T extends PrismicDocument>(documentType: string, uid?: string): Promise<PrismicDocumentResult<T>> {
    const logger = useLogger();
    const client = inject(PRISMIC_CLIENT);
    const nuxtApp = useNuxtApp();
    const route = useRoute();
    const {lang} = usePrismicLang();
    const {withLocalePrefix} = useLocalePrefix();

    try {
        const getPrismicDocument = () =>
            uid
                ? client!.getByUID<T>(documentType, uid, {lang: lang.value, fetchLinks: FETCH_LINKS})
                : client!.getSingle<T>(documentType, {lang: lang.value, fetchLinks: FETCH_LINKS});
        const document = await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        return {document: document ?? null, redirected: false};
    } catch (_) {
        logger.info(`Could not retrieve document from Prismic with parameters: [${documentType}, ${lang.value}, ${uid}]. Trying redirection...`);
    }

    const redirect = await callWithNuxt(nuxtApp, usePrismicRedirect, [route.path, lang.value, client]);
    if (!redirect) {
        return {document: null, redirected: false};
    }
    const redirectTo = () => navigateTo(withLocalePrefix(redirect.data.redirectTo as string), {redirectCode: 301});
    if (import.meta.server) {
        await callWithNuxt(nuxtApp, redirectTo);
    } else {
        void callWithNuxt(nuxtApp, redirectTo);
    }
    return {document: null, redirected: true};
}
